import { ChangeDetectionStrategy, Component, TemplateRef, computed, input } from '@angular/core';
import { ReportPage } from '@cfp/features-shared/portfolio-buildings-roadmap-report';
import { Icon } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { LloydsRoadmapPortfolioBuildingDTO, LloydsRoadmapPortfolioDetailsDTO } from 'csr-asset-app';
import { LbgReportEnergyConsumptionTable } from '../../components/lbg-report-energy-consumption-table';
import {
  ADDITIONAL_PAGE_TABLE_BODY_BUDGET_PX,
  ENERGY_CONSUMPTION_ADDRESS_TEXT_MAX_WIDTH_PX,
  estimateBuildingRowHeightPx,
  paginateBuildingsByRowHeight
} from '../report-buildings-pages/paginate-buildings-by-row-height';

/** mt-5 (~48px) + legend row (~40px). */
const LEGEND_RESERVE_PX = 96;
/** Matches energy-consumption table row min-height. */
const TOTAL_ROW_RESERVE_PX = 64;

const ENERGY_CONSUMPTION_PAGE_BUDGET_PX = ADDITIONAL_PAGE_TABLE_BODY_BUDGET_PX - LEGEND_RESERVE_PX;
const ENERGY_CONSUMPTION_LAST_PAGE_BUDGET_PX = ENERGY_CONSUMPTION_PAGE_BUDGET_PX - TOTAL_ROW_RESERVE_PX;

function pageBodyHeightPx(buildings: LloydsRoadmapPortfolioBuildingDTO[]): number {
  return buildings.reduce(
    (sum, building) =>
      sum + estimateBuildingRowHeightPx(building.displayAddress, ENERGY_CONSUMPTION_ADDRESS_TEXT_MAX_WIDTH_PX),
    0
  );
}

function paginateEnergyConsumptionBuildings(
  buildings: LloydsRoadmapPortfolioBuildingDTO[]
): LloydsRoadmapPortfolioBuildingDTO[][] {
  return paginateBuildingsByRowHeight(
    buildings,
    ENERGY_CONSUMPTION_PAGE_BUDGET_PX,
    ENERGY_CONSUMPTION_PAGE_BUDGET_PX,
    ENERGY_CONSUMPTION_ADDRESS_TEXT_MAX_WIDTH_PX
  );
}

/** Move overflow rows off the last page so the Total row still fits with the legend. */
function ensureLastPageFitsTotalRow(
  pages: LloydsRoadmapPortfolioBuildingDTO[][]
): LloydsRoadmapPortfolioBuildingDTO[][] {
  if (pages.length === 0) {
    return pages;
  }

  const result = pages.map((page) => [...page]);
  const lastPage = result[result.length - 1];
  const overflow: LloydsRoadmapPortfolioBuildingDTO[] = [];
  while (lastPage.length > 1 && pageBodyHeightPx(lastPage) > ENERGY_CONSUMPTION_LAST_PAGE_BUDGET_PX) {
    const moved = lastPage.pop();
    if (!moved) {
      break;
    }
    overflow.unshift(moved);
  }

  if (overflow.length === 0) {
    return result;
  }

  return ensureLastPageFitsTotalRow([...result, ...paginateEnergyConsumptionBuildings(overflow)]);
}

@Component({
  selector: 'cfp-lbg-report-energy-consumption-pages',
  templateUrl: 'report-energy-consumption-pages.html',
  imports: [ReportPage, LbgReportEnergyConsumptionTable, TranslateModule, Icon],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LbgReportEnergyConsumptionPages {
  public readonly customerName = input.required<string>();
  public readonly lloydsRoadmap = input.required<LloydsRoadmapPortfolioDetailsDTO>();
  public readonly headerLogo = input<string | undefined>(undefined);
  public readonly chromeFooter = input<TemplateRef<unknown> | undefined>(undefined);

  protected readonly pages = computed(() =>
    ensureLastPageFitsTotalRow(paginateEnergyConsumptionBuildings(this.lloydsRoadmap().buildings ?? []))
  );
}
